import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import NotificationSettings, { NotificationSettings as NotificationSettingsType } from './NotificationSettings';
import ChangePasswordModal from './ChangePasswordModal';
import { Settings, User, LogOut, Lock } from 'lucide-react';
import { authService } from '../services/authService';

interface LayoutProps {
  children: React.ReactNode;
}

const NOTIFICATION_SETTINGS_STORAGE_KEY = 'notification-settings-v1';

const defaultNotificationSettings: NotificationSettingsType = {
  enableBrowserNotifications: true,
  enableSoundNotifications: false,
  enableDesktopNotifications: true,
  enableMobileNotifications: false,
  notificationTypes: {
    audit_events: true,
    security_events: true,
    user_actions: true,
    system_events: true,
    bulk_operations: true,
    exports: true,
  },
  quietHours: {
    enabled: false,
    startTime: '22:00',
    endTime: '08:00',
  },
  maxNotifications: 50,
};

const loadNotificationSettings = (): NotificationSettingsType => {
  if (typeof window === 'undefined') {
    return defaultNotificationSettings;
  }

  try {
    const raw = window.localStorage.getItem(NOTIFICATION_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return defaultNotificationSettings;
    }

    const parsed = JSON.parse(raw) as Partial<NotificationSettingsType>;
    return {
      ...defaultNotificationSettings,
      ...parsed,
      notificationTypes: {
        ...defaultNotificationSettings.notificationTypes,
        ...(parsed.notificationTypes || {}),
      },
      quietHours: {
        ...defaultNotificationSettings.quietHours,
        ...(parsed.quietHours || {}),
      },
    };
  } catch {
    return defaultNotificationSettings;
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettingsType>(loadNotificationSettings);

  // Get current user from auth service
  const currentUser = authService.getUser();
  const token = authService.getToken() || '';
  const userId = currentUser?.id?.toString() || '1';
  
  // Check if user has admin role
  const isAdmin = currentUser?.access === 'admin';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(
        NOTIFICATION_SETTINGS_STORAGE_KEY,
        JSON.stringify(notificationSettings)
      );
    } catch {
      // Ignore localStorage write errors.
    }
  }, [notificationSettings]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Use replace to prevent going back
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Pricing Manager</h2>
        </div>
        <nav>
          <ul className="sidebar-nav">
            <li>
              <Link 
                to="/dashboard" 
                className={isActive('/dashboard') ? 'active' : ''}
              >
                📊 Dashboard
              </Link>
            </li>
            <li>
              <Link 
                to="/bi" 
                className={isActive('/bi') ? 'active' : ''}
              >
                📈 BI Dashboard
              </Link>
            </li>
            <li>
              <Link 
                to="/pricing-headers" 
                className={isActive('/pricing-headers') ? 'active' : ''}
              >
                📋 Pricing Headers
              </Link>
            </li>
            <li>
              <Link 
                to="/plans" 
                className={isActive('/plans') ? 'active' : ''}
              >
                📦 Plans
              </Link>
            </li>
            <li>
              <Link 
                to="/pricelists" 
                className={isActive('/pricelists') ? 'active' : ''}
              >
                💰 Price Lists
              </Link>
            </li>
            <li>
              <Link 
                to="/contracts" 
                className={isActive('/contracts') && !isActive('/contract-assignments') ? 'active' : ''}
              >
                📄 Contracts
              </Link>
            </li>
            <li>
              <Link 
                to="/contract-assignments" 
                className={isActive('/contract-assignments') ? 'active' : ''}
              >
                👥 Contract Assignments
              </Link>
            </li>
            <li>
              <Link 
                to="/customers" 
                className={isActive('/customers') ? 'active' : ''}
              >
                🏢 Customers
              </Link>
            </li>
            <li>
              <Link 
                to="/invoices" 
                className={isActive('/invoices') ? 'active' : ''}
              >
                📃 Invoices
              </Link>
            </li>
            <li>
              <Link 
                to="/templates" 
                className={isActive('/templates') ? 'active' : ''}
              >
                📑 Templates
              </Link>
            </li>
            <li>
              <Link 
                to="/features" 
                className={isActive('/features') ? 'active' : ''}
              >
                ⚙️ Features & Labels
              </Link>
            </li>
            {isAdmin && (
              <li>
                <Link 
                  to="/companies" 
                  className={isActive('/companies') ? 'active' : ''}
                >
                  🏢 Companies
                </Link>
              </li>
            )}
            {isAdmin && (
              <li>
                <Link 
                  to="/users" 
                  className={isActive('/users') ? 'active' : ''}
                >
                  ��� Users
                </Link>
              </li>
            )}
            <li>
              <Link 
                to="/audit" 
                className={isActive('/audit') ? 'active' : ''}
              >
                🔍 Audit Trail
              </Link>
            </li>
            <li>
              <Link 
                to="/data-import" 
                className={isActive('/data-import') ? 'active' : ''}
              >
                📥 Data Import
              </Link>
            </li>
            <li>
              <Link 
                to="/import-changes" 
                className={isActive('/import-changes') ? 'active' : ''}
              >
                🔄 Import Changes
              </Link>
            </li>
            {isAdmin && (
              <>
                <li>
                  <Link 
                    to="/servers" 
                    className={isActive('/servers') ? 'active' : ''}
                  >
                    🖥️ Servers
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/backups" 
                    className={isActive('/backups') ? 'active' : ''}
                  >
                    💾 Backups
                  </Link>
                </li>
              </>
            )}
            <li>
              <Link 
                to="/payment-providers" 
                className={isActive('/payment-providers') ? 'active' : ''}
              >
                💳 Payment Providers
              </Link>
            </li>
          </ul>
        </nav>
      </aside>
      
      <div className="main-container">
        {/* Top Navigation Bar */}
        <header className="top-nav">
          <div className="nav-left">
            <h1 className="page-title">
              {getPageTitle(location.pathname)}
            </h1>
          </div>
          
          <div className="nav-right">
            {/* Notification Center */}
            {token && (
              <NotificationCenter 
                token={token} 
                userId={userId}
                settings={notificationSettings}
              />
            )}
            
            {/* Settings Button */}
            <button
              onClick={() => setShowNotificationSettings(true)}
              className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
              title="Notification Settings"
            >
              <Settings className="w-6 h-6" />
            </button>
            
            {/* User Menu */}
            <div className="user-menu relative">
              <button 
                className="user-button flex items-center space-x-2 p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <User className="w-6 h-6" />
                {currentUser && (
                  <span className="text-sm font-medium">
                    {currentUser.firstName || currentUser.username}
                  </span>
                )}
              </button>
              {showUserMenu && (
                <div className="user-dropdown absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  {currentUser && (
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      <div className="font-medium">{currentUser.firstName} {currentUser.lastName}</div>
                      <div className="text-gray-500">{currentUser.email}</div>
                    </div>
                  )}
                  <button 
                    onClick={() => {
                      setShowChangePassword(true);
                      setShowUserMenu(false);
                    }} 
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 border-b"
                  >
                    <Lock className="w-4 h-4" />
                    <span>Change Password</span>
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="logout-button w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        
        <main className="main-content">
          {children}
        </main>
      </div>

      {/* Notification Settings Modal */}
      <NotificationSettings
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        currentSettings={notificationSettings}
        onSettingsChange={setNotificationSettings}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

const getPageTitle = (pathname: string): string => {
  switch (pathname) {
    case '/dashboard':
      return 'Dashboard';
    case '/pricing-headers':
      return 'Pricing Headers';
    case '/bi':
      return 'BI Dashboard';
    case '/plans':
      return 'Plans';
    case '/pricelists':
      return 'Price Lists';
    case '/contracts':
      return 'Contracts';
    case '/contract-assignments':
      return 'Contract Assignments';
    case '/customers':
      return 'Customers';
    case '/invoices':
      return 'Invoices';
    case '/companies':
      return 'Invoicing Companies';
    case '/features':
      return 'Feature Details';
    case '/users':
      return 'User Management';
    case '/audit':
      return 'Audit Trail';
    case '/data-import':
      return 'Data Import';
    case '/import-changes':
      return 'Import Changes';
    case '/servers':
      return 'Server Management';
    case '/backups':
      return 'Database Backups';
    case '/payment-providers':
      return 'Payment Providers';
    default:
      if (pathname.startsWith('/plans/')) return 'Plan Details';
      if (pathname.startsWith('/contracts/')) return 'Contract Details';
      return 'Pricing Manager';
  }
};

export default Layout;
